import { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';
import { eliminarOSuspender } from '@/lib/destruccion';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import VerSuspendidos from '@/components/VerSuspendidos';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Building2, Bed, Bath, Car, Maximize, Pencil, Trash2, ImagePlus, Percent, X, Upload, Loader2, FileDown, FileSpreadsheet, Camera, AlertTriangle } from 'lucide-react';
import useLicenseAccess from '@/hooks/useLicenseAccess';
import { ImportPropertiesModal } from '@/components/ImportPropertiesModal';
import CopyableId from '@/components/CopyableId';
import HelpTip from '@/components/HelpTip';

// Detección de imágenes externas / inseguras que podrían fallar al generar PDFs.
// Se consideran "riesgosas":
//   - URLs http:// (inseguras, bloqueadas por algunos generadores de PDF estrictos)
//   - URLs absolutas que NO apuntan a nuestro propio hosting (/uploads, /api/uploads,
//     blob:, data:, customer-assets.emergentagent.com — confiable porque es nuestro CDN)
const _SAFE_IMG_PREFIXES = ['/uploads/', '/api/uploads/', 'blob:', 'data:'];
const _SAFE_IMG_HOSTS = ['customer-assets.emergentagent.com', 'dealider.com'];

const isRiskyImageUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  const u = url.trim();
  if (!u) return false;
  if (u.startsWith('http://')) return true;          // HTTP plano → siempre riesgo
  if (_SAFE_IMG_PREFIXES.some(p => u.startsWith(p))) return false;
  if (!u.startsWith('http')) return false;            // ruta relativa interna
  try {
    const host = new URL(u).hostname;
    return !_SAFE_IMG_HOSTS.some(h => host === h || host.endsWith(`.${h}`));
  } catch { return false; }
};

const countRiskyImages = (images) =>
  (Array.isArray(images) ? images : []).filter(isRiskyImageUrl).length;

// Formato visual de banos: 2.5 -> "2 1/2", 3 -> "3", 2.25 -> "2.25"
const formatBathrooms = (val) => {
  const n = Number(val);
  if (!Number.isFinite(n) || n <= 0) return '';
  if (Number.isInteger(n)) return String(n);
  const whole = Math.floor(n);
  const frac = n - whole;
  if (Math.abs(frac - 0.5) < 0.01) return whole === 0 ? '½' : `${whole} ½`;
  if (Math.abs(frac - 0.25) < 0.01) return whole === 0 ? '¼' : `${whole} ¼`;
  if (Math.abs(frac - 0.75) < 0.01) return whole === 0 ? '¾' : `${whole} ¾`;
  return n.toFixed(2).replace(/\.?0+$/, '');
};

const TYPES = ['casa', 'departamento', 'terreno', 'oficina', 'local', 'bodega'];
const OPS = ['venta', 'renta', 'traspaso'];
// Estado de disponibilidad — reusa properties.status. El VALOR "activo" NO se
// toca (es el gate de IA/portal/campañas). Mapeo display↔valor en UN solo lugar.
const STATUS_OPTIONS = [
  { value: 'activo', label: 'Disponible' },
  { value: 'apartada', label: 'Apartada' },
  { value: 'vendida', label: 'Vendida' },
];
// Lectura legacy: el dato viejo "cerrado" se MUESTRA como Vendida (sin migrarlo).
const statusValueForSelect = (s) => (s === 'cerrado' ? 'vendida' : (s || 'activo'));
const statusLabel = (s) => (STATUS_OPTIONS.find(o => o.value === statusValueForSelect(s)) || STATUS_OPTIONS[0]).label;
// Badge SOLO para no-disponibles (Apartada=ámbar, Vendida=rojo); Disponible = sin chip.
const STATUS_BADGE = { apartada: 'bg-amber-500/90 text-black', vendida: 'bg-red-600/90 text-white' };
const statusBadgeClass = (s) => STATUS_BADGE[statusValueForSelect(s)] || '';
const BASE_AMENITIES = {
  casa: ['Alberca', 'Gimnasio', 'Jardin', 'Seguridad 24h', 'Roof Garden', 'Area de juegos', 'Pet Friendly'],
  departamento: ['Alberca', 'Gimnasio', 'Seguridad 24h', 'Roof Garden', 'Elevador', 'Pet Friendly', 'Salon de Eventos'],
  terreno: ['Seguridad 24h', 'Acceso Pavimentado', 'Servicios Incluidos', 'Dentro de Fraccionamiento'],
  oficina: ['Seguridad 24h', 'Elevador', 'Estacionamiento', 'Sala de Juntas', 'Recepcion'],
  local: ['Seguridad 24h', 'Estacionamiento', 'Mezzanine', 'Bodega'],
  bodega: ['Seguridad 24h', 'Anden de Carga', 'Acceso Vehicular', 'Oficinas Integradas'],
};
const BASE_CHARS = {
  casa: ['Amueblado', 'Cocina Integral', 'Aire Acondicionado', 'Calefaccion', 'Cuarto de Servicio', 'Balcon/Terraza', 'Vista Panoramica'],
  departamento: ['Amueblado', 'Cocina Integral', 'Aire Acondicionado', 'Balcon/Terraza', 'Vista Panoramica', 'Walk-in Closet'],
  terreno: ['Vista Panoramica', 'Plano', 'Pendiente', 'Uso Mixto', 'Uso Habitacional'],
  oficina: ['Amueblado', 'Aire Acondicionado', 'Piso Tecnico', 'Open Floor', 'Fibra Optica'],
  local: ['Amueblado', 'Aire Acondicionado', 'Fachada', 'Doble Altura'],
  bodega: ['Aire Acondicionado', 'Piso de Concreto', 'Altura 6m+', 'Sistema Contra Incendios'],
};
// Clave de dedupe — espejo de _normalize_tag del backend (NFKD → sin acentos →
// lower → trim → colapsa espacios). "Jardín" = "jardin" = "Jardin ".
const normalizeTag = (s) => (s || '')
  .normalize('NFKD')
  .replace(/\p{M}/gu, '')
  .toLowerCase()
  .trim()
  .replace(/\s+/g, ' ');

// Une chips BASE del tipo + catálogo del cliente, deduplicado por valor
// NORMALIZADO. Marca fromBase para no ponerle "x" a los predefinidos.
const buildTagSuggestions = (baseArr, catalogArr) => {
  const baseNorms = new Set((baseArr || []).map(normalizeTag));
  const seen = new Set();
  const out = [];
  for (const v of [...(baseArr || []), ...(catalogArr || [])]) {
    const n = normalizeTag(v);
    if (!n || seen.has(n)) continue;
    seen.add(n);
    out.push({ value: v, norm: n, fromBase: baseNorms.has(n) });
  }
  return out;
};

const emptyProp = {
  title: '', description: '', property_type: 'casa', operation_type: 'venta',
  price: 0, currency: 'MXN', area_m2: 0, habitable_m2: 0, bedrooms: 0, bathrooms: 0, parking_spots: 0,
  amenities: [], characteristics: [], custom_amenities: [], custom_characteristics: [], images: [],
  // Address legacy + desglose (Sprint 1.B)
  address: '',
  street: '', ext_number: '', int_number: '',
  neighborhood: '', zone: '', city: '', state: '', country: 'México', postal_code: '', zip_code: '',
  google_maps_url: '',
  // Grupo
  group_id: '', group_name: '',
  status: 'activo',
  commission_pct: 0, brought_by: '', extra_commission_pct: 0,
};

export default function PropertiesPage() {
  const { isIndependent, isAgency, isFranchise } = useLicenseAccess();
  // Dropdown "grupo de propiedades" se muestra solo para agency / franchise (no para independent).
  const showGroupField = isAgency || isFranchise;
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyProp);
  const [imageUrl, setImageUrl] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterOp, setFilterOp] = useState('all');
  const [newAmenity, setNewAmenity] = useState('');
  const [newChar, setNewChar] = useState('');
  // Catálogo acumulable de tags por cliente (bolsa única por company, NO por tipo).
  const [catalogAmenities, setCatalogAmenities] = useState([]);
  const [catalogChars, setCatalogChars] = useState([]);
  const [team, setTeam] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [pdfLoading, setPdfLoading] = useState('');
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [leads, setLeads] = useState([]);

  const downloadPdf = async (propId, title) => {
    setPdfLoading(propId);
    try {
      const r = await api.get(`/properties/${propId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Ficha_${title.replace(/\s/g, '_').substring(0, 40)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Ficha PDF descargada');
    } catch (e) {
      let detail = '';
      const blob = e?.response?.data;
      if (blob && typeof blob.text === 'function') {
        try {
          const txt = await blob.text();
          detail = JSON.parse(txt)?.detail || '';
        } catch { /* blob no era JSON parseable */ }
      }
      toast.error(detail || 'Error al generar PDF');
    }
    finally { setPdfLoading(''); }
  };

  // Fase 4.5: lo suspendido se oculta por defecto.
  const [verSuspendidos, setVerSuspendidos] = useState(false);
  const load = useCallback(() => {
    const params = {};
    if (filterType !== 'all') params.property_type = filterType;
    if (filterOp !== 'all') params.operation_type = filterOp;
    api.get('/properties', { params: { ...params, ...(verSuspendidos ? { include_suspended: true } : {}) } }).then(r => { setProperties(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [filterType, filterOp, verSuspendidos]);

  const loadCatalog = useCallback(() => {
    api.get('/property-tag-catalog').then(r => {
      setCatalogAmenities(r.data?.amenities || []);
      setCatalogChars(r.data?.characteristics || []);
    }).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/team').then(r => setTeam(r.data)).catch(() => {});
    // paginacion-lista-leads: limit=0 = modo "traer todo" (mismo comportamiento de antes).
    api.get('/leads', { params: { limit: 0 } }).then(r => setLeads(r.data?.items || [])).catch(() => {});
    loadCatalog();
  }, [loadCatalog]);

  const openNew = () => { setEditing(null); setForm(emptyProp); setImageUrl(''); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditing(p.id);
    setForm({ ...emptyProp, ...p, custom_amenities: p.custom_amenities || [], custom_characteristics: p.custom_characteristics || [] });
    setImageUrl('');
    setDialogOpen(true);
  };

  const save = async () => {
    try {
      if (editing) { await api.put(`/properties/${editing}`, form); toast.success('Propiedad actualizada'); }
      else { await api.post('/properties', form); toast.success('Propiedad creada'); }
      setDialogOpen(false);
      load();
      loadCatalog();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const remove = async (id) => {
    await eliminarOSuspender(`/properties/${id}`, {
      etiqueta: 'Propiedad',
      confirmar: '¿Retirar esta propiedad de la operación?',
      alTerminar: load,
    });
  };

  const addImage = () => {
    if (imageUrl.trim() && (form.images?.length || 0) < 10) {
      setForm({ ...form, images: [...(form.images || []), imageUrl.trim()] });
      setImageUrl('');
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = 10 - (form.images?.length || 0);
    const toUpload = files.slice(0, remaining);
    if (files.length > remaining) toast.info(`Solo se suben ${remaining} imagenes (limite 10)`);
    setUploading(true);
    const newImages = [...(form.images || [])];
    for (const file of toUpload) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await api.post('/upload/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        newImages.push(res.data.url);
      } catch (err) {
        toast.error(`Error subiendo ${file.name}`);
      }
    }
    setForm(prev => ({ ...prev, images: newImages }));
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    toast.success(`${toUpload.length} imagen(es) subida(s)`);
  };

  // Agrega un tag tecleado: lo selecciona en la propiedad (custom) Y lo suma a
  // los sugeridos locales (inmediato en la sesión). La persistencia real al
  // catálogo ocurre al GUARDAR la propiedad (el backend acumula).
  const addCustom = (field, value, setter, catalogSetter) => {
    const v = value.trim();
    if (!v) return;
    const n = normalizeTag(v);
    const arr = form[field] || [];
    if (!arr.some(x => normalizeTag(x) === n)) {
      setForm({ ...form, [field]: [...arr, v] });
    }
    if (catalogSetter) {
      catalogSetter(prev => prev.some(x => normalizeTag(x) === n) ? prev : [...prev, v]);
    }
    setter('');
  };

  const removeCustom = (field, idx) => {
    setForm({ ...form, [field]: (form[field] || []).filter((_, i) => i !== idx) });
  };

  // Selecciona/deselecciona un chip sugerido. Predefinido (fromBase) → array
  // base; sugerencia del catálogo del cliente → array custom. Match por norm.
  const toggleSuggestion = (selField, customField, sugg) => {
    const sel = form[selField] || [];
    const cust = form[customField] || [];
    const isSel = sel.some(v => normalizeTag(v) === sugg.norm) || cust.some(v => normalizeTag(v) === sugg.norm);
    if (isSel) {
      setForm({ ...form,
        [selField]: sel.filter(v => normalizeTag(v) !== sugg.norm),
        [customField]: cust.filter(v => normalizeTag(v) !== sugg.norm),
      });
    } else if (sugg.fromBase) {
      setForm({ ...form, [selField]: [...sel, sugg.value] });
    } else {
      setForm({ ...form, [customField]: [...cust, sugg.value] });
    }
  };

  // Borra una sugerencia del catálogo del cliente. NO toca las propiedades que
  // ya usan ese tag. axios URL-encodea el value (acentos/espacios) en params.
  const deleteCatalogTag = async (kind, value, catalogSetter) => {
    try {
      await api.delete('/property-tag-catalog', { params: { kind, value } });
      const n = normalizeTag(value);
      catalogSetter(prev => prev.filter(v => normalizeTag(v) !== n));
    } catch { toast.error('No se pudo borrar la sugerencia'); }
  };

  const formatPrice = (p, c) => `$${Number(p).toLocaleString('es-MX')} ${c}`;
  const amenities = BASE_AMENITIES[form.property_type] || BASE_AMENITIES.casa;
  const chars = BASE_CHARS[form.property_type] || BASE_CHARS.casa;
  // Chips sugeridos = BASE del tipo ∪ catálogo del cliente, deduplicado por norm.
  const amenitySuggestions = buildTagSuggestions(amenities, catalogAmenities);
  const charSuggestions = buildTagSuggestions(chars, catalogChars);
  const selectedAmenityNorms = new Set([...(form.amenities || []), ...(form.custom_amenities || [])].map(normalizeTag));
  const selectedCharNorms = new Set([...(form.characteristics || []), ...(form.custom_characteristics || [])].map(normalizeTag));
  const suggAmenityNorms = new Set(amenitySuggestions.map(s => s.norm));
  const suggCharNorms = new Set(charSuggestions.map(s => s.norm));
  // Tags seleccionados que NO están entre los sugeridos (custom viejos/huérfanos
  // de una propiedad en edición) — se muestran con "x" que los quita de la propiedad.
  const orphanCustomAmenities = (form.custom_amenities || []).filter(v => !suggAmenityNorms.has(normalizeTag(v)));
  const orphanCustomChars = (form.custom_characteristics || []).filter(v => !suggCharNorms.has(normalizeTag(v)));

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="properties-page">
      <div className="flex items-center justify-between mb-6 sm:mb-8 flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-xl sm:text-2xl font-bold text-white">Propiedades</h2>
          <p className="text-muted-foreground text-xs sm:text-sm mt-1">{properties.length} propiedades en catalogo</p>
        </div>
        <Button onClick={openNew} className="bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20 font-semibold text-xs sm:text-sm" data-testid="add-property-btn">
          <Plus className="w-4 h-4 mr-1 sm:mr-2" />Nueva Propiedad
        </Button>
      </div>
      <div className="flex gap-2 sm:gap-3 mb-4 sm:mb-6 flex-wrap">
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-[160px] bg-black/20 border-white/10" data-testid="filter-type"><SelectValue placeholder="Tipo" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Todos los tipos</SelectItem>{TYPES.map(t => <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filterOp} onValueChange={setFilterOp}>
          <SelectTrigger className="w-[160px] bg-black/20 border-white/10" data-testid="filter-operation"><SelectValue placeholder="Operacion" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Todas</SelectItem>{OPS.map(o => <SelectItem key={o} value={o}>{o.charAt(0).toUpperCase() + o.slice(1)}</SelectItem>)}</SelectContent>
        </Select>
        <Button
          onClick={() => setImportModalOpen(true)}
          variant="outline"
          className="border-white/10 text-white hover:bg-white/5"
          data-testid="open-import-modal-btn"
        >
          <FileSpreadsheet className="w-4 h-4 mr-1 sm:mr-2" />Importar / Exportar
        </Button>
      </div>
      {properties.length === 0 ? (
        <Card className="bg-card/50 backdrop-blur-md border-white/5"><CardContent className="py-16 text-center"><Building2 className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" /><p className="text-muted-foreground">No hay propiedades.</p></CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
          {properties.map(p => (
            <Card key={p.id} className="bg-card/50 backdrop-blur-md border-white/5 overflow-hidden hover:border-primary/30 group" style={{ transition: 'border-color 0.3s' }} data-testid={`property-card-${p.id}`}>
              <div className="h-44 bg-muted/30 relative overflow-hidden">
                {p.images?.[0] ? <img src={p.images[0]} alt={p.title} className="w-full h-full object-cover" /> : <Building2 className="w-10 h-10 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-muted-foreground/20" />}
                <div className="absolute top-3 left-3 flex gap-2">
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-primary/90 text-white">{p.operation_type}</span>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-black/60 text-white backdrop-blur-sm">{p.property_type}</span>
                  {statusBadgeClass(p.status) && (
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${statusBadgeClass(p.status)}`} data-testid={`property-status-${p.id}`}>{statusLabel(p.status)}</span>
                  )}
                </div>
                {p.commission_pct > 0 && (
                  <div className="absolute top-3 right-3">
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#A5D62C]/90 text-black flex items-center gap-1"><Percent className="w-3 h-3" />{p.commission_pct}%</span>
                  </div>
                )}
                {p.images?.length > 0 && (
                  <div className="absolute bottom-3 right-3 flex items-center gap-1 text-xs text-white bg-black/60 rounded-full px-2 py-0.5 backdrop-blur-sm" data-testid={`property-images-count-${p.id}`}>
                    <Camera className="w-3 h-3" />
                    <span>{p.images.length}</span>
                  </div>
                )}
                {countRiskyImages(p.images) > 0 && (
                  <div
                    className="absolute bottom-3 left-3 flex items-center gap-1 text-[10px] text-amber-100 bg-amber-600/90 rounded-full px-2 py-0.5 backdrop-blur-sm cursor-help"
                    title={`${countRiskyImages(p.images)} imagen(es) externa(s) o HTTP. Podrían no aparecer en el PDF generado. Considera re-subirlas desde tu equipo.`}
                    data-testid={`property-external-imgs-${p.id}`}
                  >
                    <AlertTriangle className="w-3 h-3" />
                    <span>{countRiskyImages(p.images)} ext.</span>
                  </div>
                )}
              </div>
              <CardContent className="p-4">
                <h3 className="font-heading font-semibold text-white text-base mb-1 truncate">{p.title}</h3>
                <div className="mb-1"><CopyableId id={p.id} /></div>
                <p className="text-lg font-bold gold-accent mb-2">{formatPrice(p.price, p.currency)}</p>
                <p className="text-xs text-muted-foreground mb-3 truncate">{p.address}{p.city && `, ${p.city}`}</p>
                <div className="flex items-center gap-4 text-xs text-muted-foreground mb-3">
                  {p.bedrooms > 0 && <span className="flex items-center gap-1"><Bed className="w-3.5 h-3.5" />{p.bedrooms}</span>}
                  {p.bathrooms > 0 && <span className="flex items-center gap-1"><Bath className="w-3.5 h-3.5" />{formatBathrooms(p.bathrooms)}</span>}
                  {p.parking_spots > 0 && <span className="flex items-center gap-1"><Car className="w-3.5 h-3.5" />{p.parking_spots}</span>}
                  {p.area_m2 > 0 && <span className="flex items-center gap-1"><Maximize className="w-3.5 h-3.5" />{p.area_m2}m2</span>}
                  {p.habitable_m2 > 0 && <span className="flex items-center gap-1" data-testid={`property-habitable-${p.id}`}><Maximize className="w-3.5 h-3.5" />{p.habitable_m2}m² hab.</span>}
                </div>
                <div className="flex gap-1.5 flex-wrap">
                  <Button variant="ghost" size="sm" onClick={() => downloadPdf(p.id, p.title)} disabled={pdfLoading === p.id} className="flex-1 text-xs hover:bg-blue-500/10 hover:text-blue-400" data-testid={`pdf-property-${p.id}`}>
                    {pdfLoading === p.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <FileDown className="w-3 h-3 mr-1" />}PDF
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => openEdit(p)} className="flex-1 text-xs hover:bg-[#A5D62C]/10 hover:text-[#A5D62C]" data-testid={`edit-property-${p.id}`}><Pencil className="w-3 h-3 mr-1" />Editar</Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(p.id)} className="text-xs text-destructive hover:bg-destructive/10" data-testid={`delete-property-${p.id}`}><Trash2 className="w-3 h-3" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Property Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-[#1F2833] border-white/10">
          <DialogHeader>
            <DialogTitle className="font-heading text-white">{editing ? 'Editar Propiedad' : 'Nueva Propiedad'}</DialogTitle>
            <DialogDescription className="text-muted-foreground">Completa los datos de la propiedad</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Titulo</Label>
              <Input data-testid="prop-title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Casa en Polanco" className="bg-black/20 border-white/10 focus:border-primary" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Select value={form.property_type} onValueChange={v => setForm({...form, property_type: v, amenities: [], characteristics: []})}>
                  <SelectTrigger className="bg-black/20 border-white/10" data-testid="prop-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map(t => <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Operacion</Label>
                <Select value={form.operation_type} onValueChange={v => setForm({...form, operation_type: v})}>
                  <SelectTrigger className="bg-black/20 border-white/10" data-testid="prop-op"><SelectValue /></SelectTrigger>
                  <SelectContent>{OPS.map(o => <SelectItem key={o} value={o}>{o.charAt(0).toUpperCase() + o.slice(1)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Disponibilidad</Label>
                <Select value={statusValueForSelect(form.status)} onValueChange={v => setForm({...form, status: v})}>
                  <SelectTrigger className="bg-black/20 border-white/10" data-testid="prop-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2"><Label>Precio</Label><Input data-testid="prop-price" type="number" value={form.price} onChange={e => setForm({...form, price: parseFloat(e.target.value) || 0})} className="bg-black/20 border-white/10" /></div>
              <div className="space-y-2">
                <Label>Moneda</Label>
                <Select value={form.currency} onValueChange={v => setForm({...form, currency: v})}>
                  <SelectTrigger className="bg-black/20 border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="MXN">MXN</SelectItem><SelectItem value="USD">USD</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Area m2</Label><Input data-testid="prop-area" type="number" value={form.area_m2} onChange={e => setForm({...form, area_m2: parseFloat(e.target.value) || 0})} className="bg-black/20 border-white/10" /></div>
              <div className="space-y-2">
                <Label>m² habitables</Label>
                <Input data-testid="prop-habitable" type="number" value={form.habitable_m2} onChange={e => setForm({...form, habitable_m2: parseFloat(e.target.value) || 0})} className="bg-black/20 border-white/10" />
                {Number(form.habitable_m2) > Number(form.area_m2) && Number(form.area_m2) > 0 && (
                  <p className="text-[10px] text-amber-400" data-testid="habitable-warning">El área habitable es mayor que el área total</p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2"><Label>Recamaras</Label><Input data-testid="prop-beds" type="number" value={form.bedrooms} onChange={e => setForm({...form, bedrooms: parseInt(e.target.value) || 0})} className="bg-black/20 border-white/10" /></div>
              <div className="space-y-2">
                <Label>Baños <span className="text-[10px] text-muted-foreground">(admite 0.5)</span></Label>
                <Input
                  data-testid="prop-baths"
                  type="number"
                  step="0.5"
                  min="0"
                  value={form.bathrooms}
                  onChange={e => setForm({ ...form, bathrooms: parseFloat(e.target.value) || 0 })}
                  className="bg-black/20 border-white/10"
                />
                {form.bathrooms > 0 && !Number.isInteger(form.bathrooms) && (
                  <p className="text-[10px] text-emerald-400/80" data-testid="prop-baths-display">
                    En ficha se mostrará como: <span className="font-medium">{formatBathrooms(form.bathrooms)}</span>
                  </p>
                )}
              </div>
              <div className="space-y-2"><Label>Estacionamiento</Label><Input data-testid="prop-parking" type="number" value={form.parking_spots} onChange={e => setForm({...form, parking_spots: parseInt(e.target.value) || 0})} className="bg-black/20 border-white/10" /></div>
            </div>
            {/* Commission */}
            <div className="p-4 rounded-lg bg-[#A5D62C]/[0.03] border border-[#A5D62C]/10">
              <p className="text-xs font-bold text-[#A5D62C]/80 mb-3 uppercase tracking-wider">Comision</p>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2"><Label className="text-xs inline-flex items-center gap-1">% Comision Base <HelpTip title="Este % manda en Comisiones" content="Al cerrar la oportunidad, el menú Comisiones usa este porcentaje. Si lo dejas en 0, usa el de Configuración → Comisiones. Solo inmobiliaria y franquicia." /></Label><Input data-testid="prop-commission" type="number" step="0.1" value={form.commission_pct} onChange={e => setForm({...form, commission_pct: parseFloat(e.target.value) || 0})} className="bg-black/20 border-white/10" /></div>
                <div className="space-y-2">
                  <Label className="text-xs">Traida por (promotor)</Label>
                  <Select value={form.brought_by || 'none'} onValueChange={v => setForm({...form, brought_by: v === 'none' ? '' : v})}>
                    <SelectTrigger className="bg-black/20 border-white/10" data-testid="prop-brought-by"><SelectValue placeholder="Nadie" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Ninguno (inmobiliaria)</SelectItem>
                      {team.filter(t => t.role === 'vendor').map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label className="text-xs">% Extra (si la trajo)</Label><Input data-testid="prop-extra-commission" type="number" step="0.1" value={form.extra_commission_pct} onChange={e => setForm({...form, extra_commission_pct: parseFloat(e.target.value) || 0})} className="bg-black/20 border-white/10" /></div>
              </div>
            </div>
            {/* Dirección desglosada (Sprint 1.B) */}
            <div className="space-y-3 p-4 rounded-lg bg-[#A5D62C]/[0.02] border border-[#A5D62C]/10">
              <p className="text-xs font-bold text-[#A5D62C]/80 uppercase tracking-wider">Dirección</p>
              <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                <div className="space-y-1 md:col-span-3">
                  <Label className="text-xs">Calle</Label>
                  <Input data-testid="prop-street" value={form.street || ''} onChange={e => setForm({ ...form, street: e.target.value })} className="bg-black/20 border-white/10" placeholder="Ej: Av. Reforma" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Nº Exterior</Label>
                  <Input data-testid="prop-ext-number" value={form.ext_number || ''} onChange={e => setForm({ ...form, ext_number: e.target.value })} className="bg-black/20 border-white/10" placeholder="123" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Nº Interior</Label>
                  <Input data-testid="prop-int-number" value={form.int_number || ''} onChange={e => setForm({ ...form, int_number: e.target.value })} className="bg-black/20 border-white/10" placeholder="Opcional" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Código Postal</Label>
                  <Input data-testid="prop-postal-code" value={form.postal_code || form.zip_code || ''} onChange={e => setForm({ ...form, postal_code: e.target.value, zip_code: e.target.value })} className="bg-black/20 border-white/10" placeholder="06700" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Zona / Colonia</Label>
                  <Input data-testid="prop-neighborhood" value={form.neighborhood || form.zone || ''} onChange={e => setForm({ ...form, neighborhood: e.target.value, zone: e.target.value })} className="bg-black/20 border-white/10" placeholder="Polanco" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Ciudad</Label>
                  <Input data-testid="prop-city" value={form.city || ''} onChange={e => setForm({ ...form, city: e.target.value })} className="bg-black/20 border-white/10" placeholder="Ciudad de México" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Estado</Label>
                  <Input data-testid="prop-state" value={form.state || ''} onChange={e => setForm({ ...form, state: e.target.value })} className="bg-black/20 border-white/10" placeholder="CDMX" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">País</Label>
                  <Input data-testid="prop-country" value={form.country || ''} onChange={e => setForm({ ...form, country: e.target.value })} className="bg-black/20 border-white/10" placeholder="México" />
                </div>
              </div>
              {showGroupField && (
                <div className="space-y-1" data-testid="prop-group-wrapper">
                  <Label className="text-xs">Grupo de propiedades <span className="text-[10px] text-muted-foreground">(licencia {isFranchise ? 'franquicia' : 'agencia'})</span></Label>
                  <Input data-testid="prop-group-name" value={form.group_name || ''} onChange={e => setForm({ ...form, group_name: e.target.value })} className="bg-black/20 border-white/10" placeholder="Ej: Inversión Norte, Casas Premium" />
                  <p className="text-[10px] text-muted-foreground">Sprint 2 conectará este campo con el dropdown de equipos/grupos y la ruleta de asignación de leads.</p>
                </div>
              )}
            </div>
            <div className="space-y-2"><Label>Ubicacion Google Maps</Label><Input data-testid="prop-google-maps" value={form.google_maps_url || ''} onChange={e => setForm({...form, google_maps_url: e.target.value})} placeholder="https://maps.google.com/..." className="bg-black/20 border-white/10" /><p className="text-[10px] text-muted-foreground">Pega la URL de Google Maps para mostrar la ubicacion y permitir calculo de ruta en la ficha publica.</p></div>
            <div className="space-y-2"><Label>Descripcion</Label><Textarea data-testid="prop-desc" value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3} className="bg-black/20 border-white/10" /></div>
            {/* Amenities */}
            <div className="space-y-2">
              <Label>Amenidades ({form.property_type})</Label>
              <div className="flex flex-wrap gap-2">
                {amenitySuggestions.map(s => (
                  <span key={s.norm} className={`inline-flex items-center rounded-full text-xs font-medium border transition-colors ${selectedAmenityNorms.has(s.norm) ? 'bg-primary/20 border-primary/40 text-primary' : 'bg-black/20 border-white/10 text-muted-foreground hover:border-white/20'}`}>
                    <button type="button" onClick={() => toggleSuggestion('amenities', 'custom_amenities', s)} className="px-2.5 py-1" data-testid={`amenity-${s.value}`}>{s.value}</button>
                    {!s.fromBase && (
                      <button type="button" onClick={() => deleteCatalogTag('amenity', s.value, setCatalogAmenities)} title="Quitar del catalogo" className="pr-2 -ml-1 hover:text-destructive" data-testid={`del-amenity-${s.value}`}><X className="w-3 h-3" /></button>
                    )}
                  </span>
                ))}
                {orphanCustomAmenities.map((ca, i) => (
                  <span key={`orphan-amenity-${i}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-primary/20 border border-primary/40 text-primary">
                    {ca}<button type="button" onClick={() => removeCustom('custom_amenities', (form.custom_amenities || []).indexOf(ca))} className="hover:text-destructive"><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2 mt-2">
                <Input value={newAmenity} onChange={e => setNewAmenity(e.target.value)} placeholder="Agregar amenidad..." className="bg-black/20 border-white/10 h-8 text-xs" onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addCustom('custom_amenities', newAmenity, setNewAmenity, setCatalogAmenities))} data-testid="custom-amenity-input" />
                <Button type="button" onClick={() => addCustom('custom_amenities', newAmenity, setNewAmenity, setCatalogAmenities)} variant="outline" size="sm" className="border-white/10 h-8 text-xs shrink-0" data-testid="add-custom-amenity">+ Agregar</Button>
              </div>
            </div>
            {/* Characteristics */}
            <div className="space-y-2">
              <Label>Caracteristicas ({form.property_type})</Label>
              <div className="flex flex-wrap gap-2">
                {charSuggestions.map(s => (
                  <span key={s.norm} className={`inline-flex items-center rounded-full text-xs font-medium border transition-colors ${selectedCharNorms.has(s.norm) ? 'bg-secondary/20 border-secondary/40 text-secondary' : 'bg-black/20 border-white/10 text-muted-foreground hover:border-white/20'}`}>
                    <button type="button" onClick={() => toggleSuggestion('characteristics', 'custom_characteristics', s)} className="px-2.5 py-1" data-testid={`char-${s.value}`}>{s.value}</button>
                    {!s.fromBase && (
                      <button type="button" onClick={() => deleteCatalogTag('characteristic', s.value, setCatalogChars)} title="Quitar del catalogo" className="pr-2 -ml-1 hover:text-destructive" data-testid={`del-char-${s.value}`}><X className="w-3 h-3" /></button>
                    )}
                  </span>
                ))}
                {orphanCustomChars.map((cc, i) => (
                  <span key={`orphan-char-${i}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-secondary/20 border border-secondary/40 text-secondary">
                    {cc}<button type="button" onClick={() => removeCustom('custom_characteristics', (form.custom_characteristics || []).indexOf(cc))} className="hover:text-destructive"><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2 mt-2">
                <Input value={newChar} onChange={e => setNewChar(e.target.value)} placeholder="Agregar caracteristica..." className="bg-black/20 border-white/10 h-8 text-xs" onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addCustom('custom_characteristics', newChar, setNewChar, setCatalogChars))} data-testid="custom-char-input" />
                <Button type="button" onClick={() => addCustom('custom_characteristics', newChar, setNewChar, setCatalogChars)} variant="outline" size="sm" className="border-white/10 h-8 text-xs shrink-0" data-testid="add-custom-char">+ Agregar</Button>
              </div>
            </div>
            {/* Images */}
            <div className="space-y-2">
              <Label>Imagenes ({form.images?.length || 0}/10)</Label>
              <div className="flex gap-2">
                <Input data-testid="prop-image-url" value={imageUrl} onChange={e => setImageUrl(e.target.value)} placeholder="https://..." className="bg-black/20 border-white/10 flex-1" disabled={(form.images?.length || 0) >= 10} />
                <Button type="button" onClick={addImage} variant="outline" size="sm" className="border-white/10 shrink-0" data-testid="add-image-btn" disabled={(form.images?.length || 0) >= 10}><ImagePlus className="w-4 h-4" /></Button>
              </div>
              <div className="flex items-center gap-2">
                <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleFileUpload} className="hidden" data-testid="prop-image-file" />
                <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} className="border-white/10 text-muted-foreground hover:text-white gap-2 w-full" disabled={(form.images?.length || 0) >= 10 || uploading} data-testid="upload-images-btn">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {uploading ? 'Subiendo...' : 'Subir desde dispositivo'}
                </Button>
              </div>
              {(form.images?.length || 0) >= 10 && <p className="text-xs text-amber-400">Maximo 10 imagenes alcanzado</p>}
              {countRiskyImages(form.images) > 0 && (
                <div
                  className="flex items-start gap-2 p-2.5 rounded-md bg-amber-500/10 border border-amber-500/30 text-[11px] text-amber-200"
                  data-testid="prop-form-external-imgs-warning"
                >
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <div className="leading-snug">
                    <strong>{countRiskyImages(form.images)}</strong> imagen(es) son externas o usan HTTP.
                    Pueden no aparecer al generar el PDF (los generadores estrictos bloquean recursos no seguros).
                    Recomendado: <span className="text-amber-100">re-subir desde &quot;Subir desde dispositivo&quot;</span> para alojarlas en nuestro CDN.
                  </div>
                </div>
              )}
              {form.images?.length > 0 && (
                <div className="flex gap-2 flex-wrap mt-2">
                  {form.images.map((img, i) => {
                    const risky = isRiskyImageUrl(img);
                    return (
                      <div key={i} className={`relative w-16 h-16 rounded-lg overflow-hidden border group ${risky ? 'border-amber-500/60' : 'border-white/10'}`} data-testid={`prop-form-img-${i}`}>
                        <img src={img} alt="" className="w-full h-full object-cover" />
                        <button onClick={() => setForm({...form, images: form.images.filter((_, j) => j !== i)})} className="absolute inset-0 bg-destructive/80 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"><X className="w-4 h-4" /></button>
                        {i === 0 && <span className="absolute bottom-0 left-0 right-0 bg-primary/80 text-[8px] text-center text-white py-0.5">Principal</span>}
                        {risky && (
                          <span
                            className="absolute top-0 right-0 bg-amber-500 text-black rounded-bl px-1 py-0.5 text-[8px] font-bold leading-none"
                            title="Imagen externa — podría no aparecer en el PDF"
                            data-testid={`prop-form-img-risky-${i}`}
                          >
                            EXT
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <Button onClick={save} className="w-full bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20 font-semibold" data-testid="save-property-btn">{editing ? 'Actualizar' : 'Crear'} Propiedad</Button>
          </div>
        </DialogContent>
      </Dialog>

      <ImportPropertiesModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onComplete={load}
      />
    </div>
  );
}
